import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AudienceItem } from '../../audience';
import { AudienceApiService, AudienceInsightsCosts } from '../../../../services/audience-api.service';

/**
 * Details tab of the advanced "Additional Info" card. Mostly presentational — renders the fields we
 * already hold on the selected AudienceItem (no extra fetch): core info, dates, id, filter types and
 * tags. Cost insights are the one on-demand fetch, gated behind a button since they run a BigQuery
 * cost query on the backend. (Admin-only linked-audiences from the old admin-dashboard is intentionally
 * left out for now.)
 */
@Component({
  selector: 'app-audience-details-tab',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './audience-details-tab.component.html',
  styleUrls: ['./audience-details-tab.component.scss']
})
export class AudienceDetailsTabComponent implements OnChanges {
  @Input() audience: AudienceItem | null = null;
  @Input() ownerLabel: string | null = null;

  copied = false;
  costInsights: AudienceInsightsCosts | null = null;
  costInsightsLoading = false;
  costInsightsError = false;

  constructor(private audienceApi: AudienceApiService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['audience'] && this.audience?.id !== changes['audience'].previousValue?.id) {
      this.costInsights = null;
      this.costInsightsLoading = false;
      this.costInsightsError = false;
    }
  }

  loadCostInsights(): void {
    const audienceId = this.audience?.id;
    if (!audienceId || this.costInsightsLoading) {
      return;
    }
    this.costInsightsLoading = true;
    this.costInsightsError = false;
    this.audienceApi.getAudienceInsightsCosts(audienceId).subscribe({
      next: (data) => {
        this.costInsightsLoading = false;
        this.costInsights = data;
      },
      error: () => {
        this.costInsightsLoading = false;
        this.costInsightsError = true;
      }
    });
  }

  get filterTypes(): string[] {
    const items = this.audience?.filterItems ?? [];
    return Array.from(new Set(items.map(f => f.filterType).filter((t): t is string => !!t)));
  }

  get tags(): string[] {
    return this.audience?.tags ?? [];
  }

  copyId(): void {
    const id = this.audience?.id;
    if (!id) {
      return;
    }
    const done = () => { this.copied = true; setTimeout(() => (this.copied = false), 2000); };
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(id).then(done).catch(() => { /* ignore */ });
    } else {
      done();
    }
  }
}
